import type { LoomPatternGraphProjection, LoomPatternHierarchy, LoomPatternSectionProjection } from "./loomPatternPlaybook";

export interface LoomPatternHierarchyProps {
  hierarchy: LoomPatternHierarchy;
  selectedSectionId?: string;
  onSelectSection?: (section: LoomPatternSectionProjection) => void;
}

function GraphBranch({ node, path, selectedSectionId, onSelectSection }: {
  node: LoomPatternGraphProjection;
  path: string;
  selectedSectionId?: string;
  onSelectSection?: (section: LoomPatternSectionProjection) => void;
}) {
  return (
    <li data-pattern-role={node.role} data-testid={`loom-pattern-node-${node.nodeId}`}>
      <span>{node.label}</span> <small>{node.role === "derived-entity" ? "derived instance" : node.nodeType}</small>
      {node.mirrorRelation ? <div data-testid="loom-pattern-mirror-relation">mirrors {node.mirrorRelation.sourceNodeId} about {node.mirrorRelation.axis}={node.mirrorRelation.planeOffsetM}</div> : null}
      {node.wing ? (
        <ul aria-label={`${node.label} panels`}>
          {node.wing.panels.map((panel) => (
            <li key={panel.id} data-testid={`loom-pattern-panel-${panel.panelId}`}>
              <span>Panel {panel.panelId}</span>
              <ul>
                {panel.sections.map((section) => (
                  <li key={section.id}>
                    <button
                      aria-pressed={selectedSectionId === section.id}
                      data-testid={`loom-pattern-section-${section.side}`}
                      onClick={() => onSelectSection?.(section)}
                      type="button"
                    >
                      {section.side} section · {section.family}
                    </button>
                    <small
                      data-artifact-id={section.treatment?.artifactId}
                      data-limits-source={section.limitsSource}
                      data-section-id={section.id}
                      data-treatment-state={section.treatment ? "applied" : "none"}
                      data-testid={`loom-pattern-section-${panel.panelId}-${section.side}-recipe`}
                    >
                      {section.treatment
                        ? `High-Lift applied · ${section.treatment.deviceKinds.length} device${section.treatment.deviceKinds.length === 1 ? "" : "s"}${section.treatment.leadingEdgeMorph ? " · droop nose" : ""} · ${section.treatment.producerVersion}`
                        : section.limitsSource === "section-recipe"
                          ? "Authored Loom limits · no High-Lift treatment"
                          : "Default Loom limits · no High-Lift treatment"}
                    </small>
                  </li>
                ))}
              </ul>
            </li>
          ))}
          {node.wing.mirrorInstances.map((instance) => (
            <li key={instance.id} data-pattern-role="recipe-attribute" data-testid="loom-pattern-wing-mirror-attribute">
              Mirror attribute · {instance.axis}={instance.planeOffsetM} → derived entity {instance.partnerNodeId}
            </li>
          ))}
        </ul>
      ) : null}
      {node.role !== "derived-entity" && node.inputs.length ? (
        <ul>
          {node.inputs.map((input, index) => <GraphBranch key={`${path}:${input.id}:${index}`} node={input} path={`${path}:${index}`} selectedSectionId={selectedSectionId} onSelectSection={onSelectSection} />)}
        </ul>
      ) : null}
    </li>
  );
}

export function LoomPatternHierarchyView({ hierarchy, selectedSectionId, onSelectSection }: LoomPatternHierarchyProps) {
  return (
    <nav aria-label="Aircraft Pattern hierarchy" data-pattern-id={hierarchy.id} data-state-id={hierarchy.stateId} data-testid="loom-pattern-hierarchy">
      <header>
        <strong>Aircraft Pattern</strong>
        <small>{hierarchy.documentId} · r{hierarchy.revision}</small>
      </header>
      <ul aria-label="Pattern outputs">
        {hierarchy.outputs.map((output) => (
          <li key={output.id} data-pattern-role="recipe-node" data-testid={`loom-pattern-output-${output.rootNodeId}`}>
            <span>Output · {output.rootNodeId}</span>
            <ul><GraphBranch node={output.graph} path={output.id} selectedSectionId={selectedSectionId} onSelectSection={onSelectSection} /></ul>
          </li>
        ))}
      </ul>
    </nav>
  );
}
